import { RootState } from '@/store/store'
import { selectArtifactById } from '@/artifacts/store/selectors'
import { selectFilesById } from '@/user_files/files/store/selectors'
import { ArtifactVersionAppliedTemplateRead } from '../schemas'
import { isAppliedTemplateFreshForSelection } from '@/artifact-templates/utils/applied-template-freshness'

/**
 * The sticky template choice (template_user_file_id) for a draft, or undefined if none.
 * Use the id with `selectTemplateName` to resolve a display name.
 */
export const selectTemplateSelectionForArtifact = (state: RootState, props: { artifactId: string | undefined | null }): string | undefined => {
  if (!props.artifactId) return undefined
  return state.artifactTemplatesState.templateSelectionByArtifactId[props.artifactId]
}

/**
 * The applied-template record for a version, or undefined if none has been applied.
 * Select a narrow scalar off this (e.g. its `applied_output_path`) rather than the whole
 * object, so a fresh conversation-load parse doesn't rerender consumers.
 */
export const selectAppliedTemplateForVersion = (
  state: RootState,
  props: { artifactVersionId: string | undefined | null }
): ArtifactVersionAppliedTemplateRead | undefined => {
  if (!props.artifactVersionId) return undefined
  return state.artifactTemplatesState.appliedTemplateByVersionId[props.artifactVersionId]
}

/**
 * The owning draft (artifact_id) for a version. The export hook is handed a version id
 * but selections are keyed by artifact, so this bridges the two from the artifacts store.
 */
export const selectArtifactIdForVersion = (state: RootState, props: { artifactVersionId: string | undefined | null }): string | undefined => {
  return selectArtifactById(state, props)?.artifact_id
}

/**
 * The applied-template record for a version, but only while its rendered docx still represents what
 * the user would export now — i.e. the output isn't stale. It goes stale two ways:
 *  - the draft was edited after it was applied (`version.updated_at` moves past `last_applied_at`), or
 *  - a different template is now selected than the one that produced the output.
 * Either way the caller should re-apply rather than serve/label the existing file.
 *
 * Returns undefined when nothing has been applied or the output is stale. (No `updated_at` from the
 * backend → not treated as content-stale.) Returns the stored object reference, so it needs no memoization.
 */
export const selectFreshAppliedTemplateForVersion = (
  state: RootState,
  props: { artifactVersionId: string | undefined | null }
): ArtifactVersionAppliedTemplateRead | undefined => {
  const applied = selectAppliedTemplateForVersion(state, props)
  if (!applied) return undefined

  // Stale if the draft was edited after the template was applied — the output no longer matches it.
  const updatedAt = selectArtifactById(state, props)?.updated_at
  if (updatedAt != null && updatedAt > applied.last_applied_at) return undefined

  // Stale if the draft's selected template has since changed: applying B doesn't make A's output
  // "applied". A null applied template means the template file was deleted post-export; keep that
  // output only while no replacement template is selected.
  const artifactId = selectArtifactIdForVersion(state, props)
  const selectedTemplateId = selectTemplateSelectionForArtifact(state, { artifactId })
  if (
    !isAppliedTemplateFreshForSelection({
      appliedTemplateUserFileId: applied.template_user_file_id,
      selectedTemplateUserFileId: selectedTemplateId,
    })
  ) {
    return undefined
  }

  return applied
}

/**
 * A template's display name from its UserFile id. Template state is stored by id only, so
 * names come from the loaded UserFiles. Returns undefined when the file isn't loaded or has
 * been deleted — callers then render the selected/applied state with no label.
 */
export const selectTemplateName = (state: RootState, props: { templateUserFileId: string | undefined | null }): string | undefined => {
  if (!props.templateUserFileId) return undefined
  return selectFilesById(state)[props.templateUserFileId]?.original_filename
}
